import React from 'react';
import { statusTone, platformMeta, priorityMeta, humanDate, dueBadge } from './mccConstants';

/** A compact status pill with matching colour. */
export const StatusPill = ({ status, size = 'md' }) => {
  const t = statusTone(status);
  const cls = size === 'sm'
    ? 'px-2 py-0.5 text-[10px]'
    : 'px-2.5 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold uppercase tracking-wide ${cls}`}
      style={{ background: t.bg, color: t.ink }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.accent }} />
      {t.label}
    </span>
  );
};

export const PriorityPill = ({ priority }) => {
  const p = priorityMeta(priority);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: `${p.tone}20`, color: p.tone }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.tone }} />
      {p.l}
    </span>
  );
};

/** Circular platform icon chip. */
export const PlatformIcon = ({ platform, size = 24 }) => {
  const meta = platformMeta(platform);
  if (!meta) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500"
        style={{ width: size, height: size }}
      >?</span>
    );
  }
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white"
      style={{ width: size, height: size, background: meta.tone }}
      title={meta.l}
    >
      <Icon style={{ width: size * 0.58, height: size * 0.58 }} />
    </span>
  );
};

/** Small metric card (used on dashboards). */
export const StatCard = ({ label, value, sublabel, tone = 'slate', icon: Icon, onClick, testid }) => {
  const toneMap = {
    slate: 'bg-white border-slate-200',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    violet: 'bg-violet-50 border-violet-200 text-violet-900',
    sky: 'bg-sky-50 border-sky-200 text-sky-900',
  };
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      className={`${toneMap[tone] || toneMap.slate} border rounded-xl px-4 py-3 ${onClick ? 'cursor-pointer hover:shadow-md transition' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">{label}</p>
          <p className="text-2xl font-bold mt-0.5 tabular-nums">{value}</p>
          {sublabel ? <p className="text-[11px] opacity-70 mt-0.5 truncate">{sublabel}</p> : null}
        </div>
        {Icon ? <Icon className="w-5 h-5 opacity-60 flex-shrink-0" /> : null}
      </div>
    </div>
  );
};

/** Small assignee avatar circle. */
export const Avatar = ({ name, size = 26 }) => {
  const initials = (name || '?')
    .split(' ').filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('');
  const hue = Math.abs((name || '?').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 360;
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-semibold text-white flex-shrink-0"
      style={{
        width: size, height: size,
        fontSize: size * 0.36,
        background: `hsl(${hue}, 55%, 45%)`,
      }}
      title={name || ''}
    >{initials}</span>
  );
};

/** Stack of overlapping avatars. */
export const AvatarStack = ({ names = [], max = 3, size = 22 }) => {
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <span className="inline-flex items-center">
      {shown.map((n, i) => (
        <span key={i} style={{ marginLeft: i === 0 ? 0 : -6 }} className="ring-2 ring-white rounded-full">
          <Avatar name={n} size={size} />
        </span>
      ))}
      {extra > 0 && (
        <span
          className="ring-2 ring-white rounded-full bg-slate-200 text-slate-700 inline-flex items-center justify-center font-semibold"
          style={{ width: size, height: size, marginLeft: -6, fontSize: size * 0.4 }}
        >+{extra}</span>
      )}
    </span>
  );
};

export const DueBadge = ({ dueIso }) => {
  const b = dueBadge(dueIso);
  if (!b) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold"
      style={{ background: b.bg, color: b.tone }}
    >
      {b.label}
    </span>
  );
};

export const MetaLine = ({ children }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
    {children}
  </div>
);

export const SectionHeader = ({ title, subtitle, icon: Icon, right }) => (
  <div className="flex items-start justify-between gap-4 mb-4">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="w-5 h-5 text-indigo-600" /> : null}
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h1>
      </div>
      {subtitle ? <p className="text-sm text-slate-500 mt-1">{subtitle}</p> : null}
    </div>
    {right ? <div className="flex-shrink-0">{right}</div> : null}
  </div>
);

export const EmptyState = ({ icon: Icon, title, body, cta }) => (
  <div className="text-center py-16 px-4 border border-dashed border-slate-300 rounded-2xl bg-slate-50/60">
    {Icon ? (
      <div className="w-12 h-12 mx-auto rounded-full bg-slate-200 text-slate-600 flex items-center justify-center mb-3">
        <Icon className="w-6 h-6" />
      </div>
    ) : null}
    <p className="text-slate-900 font-semibold">{title}</p>
    {body ? <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{body}</p> : null}
    {cta ? <div className="mt-4">{cta}</div> : null}
  </div>
);

/** Displays a small formatted metadata row (Person, Due, Platforms, etc.) */
export const InfoRow = ({ label, value }) => (
  <div className="flex items-start gap-2 text-[12px]">
    <span className="text-slate-500 font-medium uppercase tracking-wider text-[10px] pt-0.5 w-24 flex-shrink-0">{label}</span>
    <span className="text-slate-900 min-w-0">{value ?? '—'}</span>
  </div>
);

export const humanDateExport = humanDate;
